import React from 'react';

// One tooth, drawn the way it sits in the jaw: the crown towards the bite and
// the roots away from it, shaped by what kind of tooth it is. Under it, the
// tooth seen from the biting surface, divided into its five faces, which is
// where a filling or a cavity is actually marked.
//
// Drawn once, for an upper tooth (roots up, the surface diagram beneath the
// crown); a lower tooth is the same drawing turned over, so in both jaws the
// diagram sits on the side of the bite and the roots point away from it.
// Everything is in a 40 x 100 box: for an upper tooth roots 2-40, crown 38-70,
// diagram 76-98.

// What kind of tooth an FDI number is. The second digit counts from the
// midline: 1-2 incisors, 3 the canine, 4-5 premolars, 6-8 molars. A child has
// no premolars - 4 and 5 are their molars.
export const toothKind = (number) => {
    const text = String(number);
    const position = Number(text[1]);
    const primary = Number(text[0]) >= 5;

    if (position <= 2) return 'incisor';
    if (position === 3) return 'canine';
    if (primary) return 'molar';
    return position <= 5 ? 'premolar' : 'molar';
};

export const isUpperTooth = (number) => ['1', '2', '5', '6'].includes(String(number)[0]);

// The patient's right side is drawn on the left of the chart.
export const isRightSide = (number) => ['1', '4', '5', '8'].includes(String(number)[0]);

// How wide each kind is drawn, relative to a premolar. Molars are the widest
// teeth in the mouth and the chart reads wrong if they are not.
export const TOOTH_WIDTH = { incisor: 0.86, canine: 0.9, premolar: 1, molar: 1.32 };

const CROWN = {
    incisor: 'M10 39 C8.5 49 9 61 12.5 67.5 Q20 70.5 27.5 67.5 C31 61 31.5 49 30 39 Z',
    canine: 'M9 39 C7.5 49 10 59 20 70 C30 59 32.5 49 31 39 Z',
    premolar: 'M7.5 39 C5.5 49 6.5 61 11.5 66.5 Q16 69.5 20 64.5 Q24 69.5 28.5 66.5 C33.5 61 34.5 49 32.5 39 Z',
    molar: 'M4 39 C1.5 49 2.5 61 7.5 66.5 Q11.5 69.5 14.5 65 Q20 69.5 25.5 65 Q28.5 69.5 32.5 66.5 C37.5 61 38.5 49 36 39 Z'
};

const ROOT = {
    incisor: 'M12 40 C13 25 17 9 20 3.5 C23 9 27 25 28 40 Z',
    canine: 'M11.5 40 C12.5 22 17 6 20 1.5 C23 6 27.5 22 28.5 40 Z',
    premolar: 'M10.5 40 C11.5 25 14.5 11 17.5 5 C19 9.5 21 9.5 22.5 5 C25.5 11 28.5 25 29.5 40 Z',
    // Three roots above, two below - the difference a dentist looks for first.
    molarUpper: 'M6 40 C6 26 8 11 11 5.5 C14 11 15 26 16 35 C17 25 18.5 13 20 9 C21.5 13 23 25 24 35 C25 26 26 11 29 5.5 C32 11 34 26 34 40 Z',
    molarLower: 'M6 40 C6 25 9 9 13 4.5 C16 11 18 27 19 35 L21 35 C22 27 24 11 27 4.5 C31 9 34 25 34 40 Z'
};

// The canals a root filling follows, one per root.
const CANALS = {
    incisor: ['M20 38 L20 9'],
    canine: ['M20 38 L20 7'],
    premolar: ['M17.5 38 L17.5 10', 'M22.5 38 L22.5 10'],
    molarUpper: ['M11 38 L11 10', 'M20 38 L20 13', 'M29 38 L29 10'],
    molarLower: ['M13 38 L13 9', 'M27 38 L27 9']
};

// The five faces, as the tooth is seen from the bite: a square with the
// occlusal face in the middle and the four sides around it.
const FACE = {
    top: 'M9 76 L31 76 L25.5 81.5 L14.5 81.5 Z',
    bottom: 'M9 98 L31 98 L25.5 92.5 L14.5 92.5 Z',
    left: 'M9 76 L14.5 81.5 L14.5 92.5 L9 98 Z',
    right: 'M31 76 L25.5 81.5 L25.5 92.5 L31 98 Z',
    centre: 'M14.5 81.5 L25.5 81.5 L25.5 92.5 L14.5 92.5 Z'
};

// Which face of the diagram each surface is. Mesial is always the side
// nearer the midline, so it swaps with distal between the two halves of the
// mouth; the cheek side is drawn outwards, away from the other jaw.
const facesFor = (number) => {
    const upper = isUpperTooth(number);
    const right = isRightSide(number);
    return {
        O: 'centre',
        M: right ? 'right' : 'left',
        D: right ? 'left' : 'right',
        B: upper ? 'top' : 'bottom',
        L: upper ? 'bottom' : 'top'
    };
};

const OUTLINE = '#8fa3b3';
const ENAMEL = '#ffffff';
const DENTINE = '#f3ead8';

const ToothShape = ({ number, condition, surfaces = [], color, note = false }) => {
    const kind = toothKind(number);
    const upper = isUpperTooth(number);
    const rootKey = kind === 'molar' ? (upper ? 'molarUpper' : 'molarLower') : kind;
    const faces = facesFor(number);
    const marked = new Set(surfaces.map((surface) => faces[surface]).filter(Boolean));

    const gone = condition === 'extracted' || condition === 'missing';
    const implant = condition === 'implant';
    // Conditions that are the whole crown rather than a mark on it.
    const capped = ['crown', 'bridge', 'veneer', 'implant'].includes(condition);
    const tint = color || OUTLINE;

    const crownFill = () => {
        if (gone) return 'none';
        if (capped) return tint;
        if (condition === 'healthy') return `${tint}33`;
        return ENAMEL;
    };

    return (
        <svg
            className="tooth-svg"
            viewBox="0 0 40 100"
            // The drawing is decoration: the button around it carries the
            // number and the description.
            aria-hidden="true"
            focusable="false"
            preserveAspectRatio="xMidYMid meet"
        >
            {/* A lower tooth is the same tooth turned over, roots down. */}
            <g transform={upper ? undefined : 'matrix(1 0 0 -1 0 100)'} opacity={gone ? 0.35 : 1}>
                {implant ? (
                    <g stroke={tint} strokeWidth="1.6" strokeLinecap="round" fill="none">
                        <path d="M16 40 L17 12 Q20 6 23 12 L24 40 Z" fill={`${tint}33`} />
                        <path d="M14.5 33 H25.5 M15 27 H25 M15.5 21 H24.5 M16 15 H24" />
                    </g>
                ) : (
                    <path
                        d={ROOT[rootKey]}
                        fill={gone ? 'none' : DENTINE}
                        stroke={OUTLINE}
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                        strokeDasharray={condition === 'impacted' || gone ? '3 2' : undefined}
                    />
                )}

                {condition === 'rootCanal' && CANALS[rootKey].map((canal) => (
                    <path key={canal} d={canal} stroke={tint} strokeWidth="2.4" strokeLinecap="round" />
                ))}

                <path
                    d={CROWN[kind]}
                    fill={crownFill()}
                    fillOpacity={capped && condition !== 'implant' ? 0.85 : 1}
                    stroke={condition && !gone ? tint : OUTLINE}
                    strokeWidth={condition && !gone ? 1.9 : 1.3}
                    strokeLinejoin="round"
                    strokeDasharray={condition === 'impacted' || gone ? '3 2' : undefined}
                />

                {/* A root-filled tooth has its crown opened too. */}
                {condition === 'rootCanal' && <circle cx="20" cy="50" r="4.2" fill={tint} />}
                {condition === 'caries' && <circle cx="20" cy="53" r="5.2" fill={tint} />}
                {condition === 'filling' && <rect x="13.5" y="46" width="13" height="11" rx="3" fill={tint} />}
                {condition === 'fracture' && (
                    <path d="M13 42 L21 50 L16 55 L25 66" stroke={tint} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                )}
                {condition === 'mobile' && (
                    <path d="M5 54 L1 58 L5 62 M35 54 L39 58 L35 62" stroke={tint} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                )}
                {condition === 'bridge' && <path d="M0 47 H40" stroke={tint} strokeWidth="3" />}
            </g>

            {/* Marked for extraction, or already out: struck through. */}
            {(gone || condition === 'toExtract') && (
                <path
                    d={upper ? 'M6 6 L34 68 M34 6 L6 68' : 'M6 32 L34 94 M34 32 L6 94'}
                    stroke={tint}
                    strokeWidth="2.4"
                    strokeLinecap="round"
                />
            )}

            <g
                stroke={OUTLINE}
                strokeWidth="1"
                strokeLinejoin="round"
                opacity={gone ? 0.35 : 1}
                transform={upper ? undefined : 'translate(0 -74)'}
            >
                {Object.entries(FACE).map(([face, path]) => (
                    <path key={face} d={path} fill={marked.has(face) ? tint : ENAMEL} />
                ))}
            </g>

            {note && <circle className="tooth-note-mark" cx="35" cy={upper ? 6 : 94} r="3" fill="#1f2a37" />}
        </svg>
    );
};

export default ToothShape;
